// The web Book composer (Figma eWr6sqSGfDufrRNUMjbtK4 `Web / Books / Composer modal` 2767:1081, Dark 2767:9701): create a
// BOOK post, or edit one from its ⋯ menu. Like the event composer, Post is never disabled: a tap with missing or invalid
// fields shows the red reason under each, and a change to a field clears its error.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlignLeft, Baby, BookOpen, Calendar, Euro, HandHeart, Languages, ShoppingBag, Type, User, X, type LucideIcon } from "lucide-react";
import { ContentError, createPostV2, getPostV2, updatePostV2, type PostV2 } from "../../api/content";
import { uploadBookPdf } from "../../api/books";
import { IMAGE_ACCEPT, SvgTooLargeError, uploadImage } from "../../api/upload";
import { invalidateNewsPosted } from "../../hooks/useWallFeed";
import { contentErrorText } from "../wall/contentErrorText";
import { useComposerDraftStore } from "../../store/composerDraftStore";
import LiveConfirmDialog from "../stream/LiveConfirmDialog";
import { MAX_PDF_BYTES, bookDraftOf, bookErrors, newBookDraft, toBookInput, type BookDraft, type BookField } from "./bookDraft";

interface Props {
  /** The BOOK post to edit; null = a new book. */
  existing: PostV2 | null;
  onClose(): void;
  onSaved(post: PostV2): void;
  /** New post only: the "Post to" chips open another composer (the host swaps the modal). */
  onSwitch?(kind: "personal" | "event" | "news" | "blog" | "apopilo"): void;
}

type PdfProblem = "toobig" | "failed" | "uploading" | null;

// Field row (Figma "Field · …"): an icon, then the text; the row is the elevated well.
const ROW = "flex items-center gap-3 rounded-lg bg-[var(--bg-elevated)] p-3 text-[var(--foreground)] focus-within:ring-2 focus-within:ring-[var(--primary-text)]";
const INPUT = "min-w-0 flex-1 bg-transparent text-base text-[var(--foreground)] outline-none placeholder:text-[var(--foreground-subtle)]";
const SECTION = "text-sm font-medium text-[var(--foreground-muted)]";
const PILL = "h-12 shrink-0 rounded-full border border-[var(--border-strong)] bg-[var(--bg-surface)] px-6 text-sm font-bold text-[var(--foreground)] disabled:opacity-45";
const LINK = "text-sm font-bold text-[var(--primary)] dark:text-[var(--primary-on-surface)] cursor-pointer";

function Err({ id, children }: { id: string; children: ReactNode }) {
  return <p data-testid={`book-composer-${id}-error`} role="alert" className="text-xs text-[var(--destructive-text)]">{children}</p>;
}

/** An edit opened from a list copy that lacks the book facts reads the post again before showing the form. */
export default function BookComposerModal(props: Props) {
  const { t } = useTranslation();
  const ex = props.existing;
  const needsFull = ex != null && ex.book == null;
  const full = useQuery({ queryKey: ["postEditV2", ex?.id ?? null], queryFn: () => getPostV2(ex!.id), enabled: needsFull, retry: false, gcTime: 0 });
  if (!needsFull) return <BookComposer {...props} />;
  if (full.data?.book) return <BookComposer {...props} existing={full.data} />;
  const failed = full.isError || (full.data != null && full.data.book == null);
  return (
    <div role="dialog" aria-modal="true" aria-label={t("book_composer_edit", "Edit book")} data-book-modal data-testid="book-composer-loading"
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-[color-mix(in_srgb,var(--foreground)_40%,transparent)]">
      <div className="flex w-[400px] max-w-full flex-col items-center gap-3 rounded-2xl bg-[var(--bg-surface)] p-6 shadow-xl">
        {failed ? (
          <>
            <p role="alert" className="text-sm text-[var(--destructive-text)]">
              {contentErrorText(t, full.error instanceof ContentError ? full.error.code : "UNKNOWN", t("wall_error_UNKNOWN", "Something went wrong — try again"))}
            </p>
            <button type="button" className={LINK} onClick={props.onClose}>{t("wall_close", "Close")}</button>
          </>
        ) : (
          <span aria-busy="true" className="text-sm text-[var(--foreground-muted)]">…</span>
        )}
      </div>
    </div>
  );
}

function BookComposer({ existing, onClose, onSaved, onSwitch }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const initial = useRef<BookDraft>(existing ? bookDraftOf(existing) : newBookDraft());
  const [draft, setDraft] = useState<BookDraft>(initial.current);
  const [shown, setShown] = useState<Partial<Record<BookField, true>>>({});
  const [pdfProblem, setPdfProblem] = useState<PdfProblem>(null);
  const [pdfProgress, setPdfProgress] = useState<number | null>(null);
  const [coverBusy, setCoverBusy] = useState(false);
  /** Why the cover did not upload: "svg" = an SVG over file-stc's 1 MiB cap (refused before upload). */
  const [coverFailed, setCoverFailed] = useState<false | "failed" | "svg">(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const pdfInput = useRef<HTMLInputElement>(null);
  const coverInput = useRef<HTMLInputElement>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const [photosBusy, setPhotosBusy] = useState(false);
  const [photosFailed, setPhotosFailed] = useState(false);
  // The page count last taken from a PDF: a pages value still equal to it follows the next PDF; a typed one stays.
  const autoPages = useRef(initial.current.pdf && initial.current.pdf.pages > 0 && initial.current.pages === String(initial.current.pdf.pages) ? initial.current.pages : "");
  const uploadingPdf = pdfProgress != null;
  const discarded = useRef(false);

  // Dirty = changed fields or an upload in flight; the X, a stray backdrop tap or Escape asks before throwing it away
  // (same rule as the post editor), a clean form closes at once.
  const dirty = uploadingPdf || coverBusy || photosBusy || JSON.stringify(draft) !== JSON.stringify(initial.current);
  useEffect(() => {
    useComposerDraftStore.getState().set({ dirty });
  }, [dirty]);
  useEffect(() => () => useComposerDraftStore.getState().reset(), []);
  const pendingDiscard = useComposerDraftStore((s) => s.pending);
  const guardedClose = () => useComposerDraftStore.getState().guard(() => { discarded.current = true; onClose(); });
  const guardedCloseRef = useRef(guardedClose);
  guardedCloseRef.current = guardedClose;
  useEffect(() => {
    useComposerDraftStore.getState().set({ requestClose: () => guardedCloseRef.current() });
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !e.defaultPrevented) guardedCloseRef.current(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /** Edits a field; its shown error (if any) goes away at once. */
  function patch(p: Partial<BookDraft>, field?: BookField) {
    setDraft((d) => ({ ...d, ...p }));
    if (field) setShown((s) => { const { [field]: _gone, ...rest } = s; return rest; });
  }

  async function pickPdf(file: File | undefined) {
    if (!file) return;
    setShown((s) => { const { pdf: _gone, ...rest } = s; return rest; });
    if (file.size > MAX_PDF_BYTES) {
      setPdfProblem("toobig");
      return;
    }
    setPdfProblem(null);
    setPdfProgress(0);
    try {
      const info = await uploadBookPdf(file, { onProgress: (sent, total) => setPdfProgress(total > 0 ? Math.round((sent / total) * 100) : 0) });
      setPdfProblem(null); // a "wait for the upload" shown meanwhile is stale now
      const count = info.pageCount ? String(info.pageCount) : "";
      setDraft((d) => {
        const follows = d.pages.trim() === "" || d.pages === autoPages.current;
        if (follows && count) autoPages.current = count;
        return { ...d, pdf: { fileId: info.id, name: file.name, pages: info.pageCount ?? 0 }, pages: follows && count ? count : d.pages };
      });
    } catch {
      setPdfProblem("failed");
    } finally {
      setPdfProgress(null);
    }
  }

  async function pickCover(file: File | undefined) {
    if (!file) return;
    setCoverFailed(false);
    setCoverBusy(true);
    try {
      const info = await uploadImage(file);
      patch({ cover: { fileId: info.id, url: info.url } });
    } catch (err) {
      setCoverFailed(err instanceof SvgTooLargeError ? "svg" : "failed");
    } finally {
      setCoverBusy(false);
    }
  }

  async function pickPhotos(files: FileList | null) {
    if (!files || files.length === 0) return;
    const picked = Array.from(files);
    setPhotosFailed(false);
    setPhotosBusy(true);
    try {
      for (const f of picked) {
        const info = await uploadImage(f);
        setDraft((d) => ({ ...d, photos: [...d.photos, { fileId: info.id, url: info.url }] }));
      }
    } catch {
      setPhotosFailed(true);
    } finally {
      setPhotosBusy(false);
    }
  }

  async function submit() {
    if (submitting) return;
    setSubmitError(null);
    const errs = bookErrors(draft);
    if (uploadingPdf) {
      setPdfProblem("uploading");
      return;
    }
    if (Object.keys(errs).length > 0 || coverBusy || photosBusy) {
      setShown(errs);
      if (coverBusy || photosBusy) setSubmitError("BOOK_COVER_UPLOADING");
      return;
    }
    setSubmitting(true);
    try {
      const { title, description, items, book } = toBookInput(draft);
      const input = { type: "BOOK" as const, title, description, items, book };
      const saved = existing ? await updatePostV2(existing.id, input) : await createPostV2(input);
      invalidateNewsPosted(qc);
      discarded.current = true;
      onSaved(saved);
    } catch (e) {
      setSubmitError(e instanceof ContentError ? e.code : "UNKNOWN");
    } finally {
      setSubmitting(false);
    }
  }

  /** A text row: icon + input; `err` ties it to a validated field (red reason under it). */
  const row = (field: keyof BookDraft & string, Icon: LucideIcon, label: string, opts: { err?: BookField; errText?: string; max: number; inputMode?: "numeric" | "decimal" | "url"; placeholder?: string }) => (
    <div className="flex flex-col gap-1">
      <label className={ROW}>
        <Icon size={22} className="shrink-0 text-[var(--foreground-muted)]" aria-hidden />
        <input data-testid={`book-composer-${field}`} className={INPUT} maxLength={opts.max} inputMode={opts.inputMode}
          aria-label={label} placeholder={opts.placeholder ?? label} value={draft[field] as string}
          onChange={(e) => patch({ [field]: e.target.value } as Partial<BookDraft>, opts.err)} />
      </label>
      {opts.err && shown[opts.err] && <Err id={field}>{opts.errText}</Err>}
    </div>
  );

  const heading = existing ? t("book_composer_edit", "Edit book") : t("book_composer_new", "New post");
  const pdfErrorText =
    pdfProblem === "toobig" ? t("book_composer_pdf_too_big", "The PDF can be at most 100 MB")
      : pdfProblem === "failed" ? t("book_composer_pdf_failed", "The PDF didn't upload — try again")
        : pdfProblem === "uploading" ? t("book_composer_pdf_wait", "Wait for the PDF to finish uploading")
          : t("book_composer_pdf_required", "Add the book's PDF");
  const submitText =
    submitError === "BOOK_COVER_UPLOADING" ? t("book_composer_cover_wait", "Wait for the cover to finish uploading")
      : submitError ? contentErrorText(t, submitError, t("wall_error_UNKNOWN", "Something went wrong — try again")) : null;
  const kinds: { id: "personal" | "event" | "news" | "blog" | "book" | "apopilo"; label: string }[] = [
    { id: "personal", label: t("personal_tab_personal", "Personal") },
    { id: "event", label: t("personal_tab_events", "Events") },
    { id: "news", label: t("personal_upload_news_short", "News") },
    { id: "blog", label: t("personal_tab_blog", "Blog") },
    { id: "book", label: t("personal_tab_books", "Books") },
    { id: "apopilo", label: t("personal_tab_apopilo", "Apopilo") },
  ];
  const hasItems = draft.pdf != null || uploadingPdf || draft.cover != null || coverBusy || draft.photos.length > 0 || photosBusy;

  const discardDialog =
    pendingDiscard &&
    createPortal(
      <div style={{ display: "contents" }} onClick={(e) => e.stopPropagation()}>
        <LiveConfirmDialog
          testId="discard-draft-dialog"
          zClass="z-[2200]"
          message={t("wall_discard_draft_message", "Discard this post? Your changes will be lost.")}
          confirmLabel={t("wall_discard_draft_confirm", "Discard")}
          cancelLabel={t("wall_discard_draft_keep", "Keep editing")}
          confirmTestId="discard-draft-confirm"
          onConfirm={() => useComposerDraftStore.getState().confirmDiscard()}
          onCancel={() => useComposerDraftStore.getState().cancelDiscard()}
        />
      </div>,
      document.body,
    );

  return (
    <div role="dialog" aria-modal="true" aria-label={heading} data-book-modal data-testid="book-composer"
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-[color-mix(in_srgb,var(--foreground)_40%,transparent)]"
      onClick={() => guardedCloseRef.current()}>
      <div className="flex max-h-[90vh] w-[560px] max-w-full flex-col overflow-y-auto rounded-2xl bg-[var(--bg-surface)] shadow-xl" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center gap-4 px-4 py-3">
          <button type="button" aria-label={t("wall_close", "Close")} className="text-[var(--foreground)]" onClick={() => guardedCloseRef.current()}>
            <X size={24} aria-hidden />
          </button>
          <h2 className="m-0 flex-1 text-lg font-bold leading-tight text-[var(--foreground)]">{heading}</h2>
          <button type="button" data-testid="book-composer-submit" aria-busy={submitting} onClick={() => void submit()}
            className="h-12 rounded-full bg-[var(--primary)] px-[26px] text-sm font-bold text-[var(--primary-foreground)] aria-busy:opacity-60">
            {existing ? t("wall_save_button", "Save") : t("wall_post_button", "Post")}
          </button>
        </header>

        <div className="flex flex-col gap-3 px-4 pb-5 pt-4">
          {!existing && (
            <>
              <p className={`m-0 ${SECTION}`}>{t("composer_post_to", "Post to")}</p>
              <div data-testid="book-composer-kinds" className="flex flex-wrap gap-2">
                {kinds.map(({ id, label }) => {
                  const selected = id === "book";
                  const open = id === "personal" || id === "event" || id === "news" || id === "blog" || id === "apopilo";
                  return (
                    <button key={id} type="button" data-testid={`book-composer-kind-${id}`} aria-pressed={selected}
                      disabled={!selected && (!open || !onSwitch)} title={!selected && !open ? t("personal_soon", "Soon") : undefined}
                      onClick={() => { if (open && onSwitch) useComposerDraftStore.getState().guard(() => { discarded.current = true; onSwitch(id); }); }}
                      className={`rounded-full px-[14px] py-2 text-sm font-medium disabled:cursor-default ${
                        selected ? "bg-[var(--highlight)] text-[var(--highlight-foreground)]" : "border border-[var(--border-strong)] text-[var(--foreground)] disabled:opacity-60"
                      }`}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div className="flex flex-col gap-1">
            <label className={ROW}>
              <Type size={22} className="shrink-0 text-[var(--foreground-muted)]" aria-hidden />
              <input data-testid="book-composer-title" data-autofocus className={INPUT} maxLength={120} aria-label={t("book_composer_title", "Title")}
                placeholder={`${t("book_composer_title", "Title")} *`} value={draft.title} onChange={(e) => patch({ title: e.target.value }, "title")} />
            </label>
            {shown.title && <Err id="title">{t("book_composer_title_required", "Add a title")}</Err>}
          </div>
          <label className={ROW}>
            <AlignLeft size={22} className="shrink-0 text-[var(--foreground-muted)]" aria-hidden />
            <textarea data-testid="book-composer-description" className={`${INPUT} min-h-[48px] resize-y`} maxLength={4096} rows={2}
              aria-label={t("book_composer_description", "Description")} placeholder={t("book_composer_description_ph", "What is this book about?")}
              value={draft.description} onChange={(e) => patch({ description: e.target.value })} />
          </label>

          {/* The one upload area (Figma Items): PDF and cover. */}
          <div data-testid="book-composer-items" className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-[var(--border-strong)] p-4">
            {!hasItems && (
              <>
                <p className="m-0 text-sm font-bold text-[var(--foreground)]">{t("book_composer_nothing", "Nothing here yet")}</p>
                <p className="m-0 w-[280px] max-w-full text-center text-xs text-[var(--foreground-muted)]">{t("book_composer_items_hint", "Add the book as a PDF and a cover photo.")}</p>
              </>
            )}
            {(draft.pdf || uploadingPdf) && (
              <div className="flex w-full items-center gap-3 text-[var(--foreground)]">
                <BookOpen size={22} className="shrink-0 text-[var(--foreground-muted)]" aria-hidden />
                {uploadingPdf ? (
                  <span data-testid="book-composer-pdf-progress" className="min-w-0 flex-1 text-base text-[var(--foreground-muted)]">
                    {t("book_composer_uploading", { percent: pdfProgress, defaultValue: "Uploading… {{percent}}%" })}
                  </span>
                ) : (
                  <span data-testid="book-composer-pdf-name" className="min-w-0 flex-1 truncate text-base">
                    {draft.pdf!.name || t("book_composer_pdf", "PDF")}
                    {draft.pdf!.pages > 0 && <span className="text-[var(--foreground-muted)]">{" · "}{t("book_composer_pages_count", { count: draft.pdf!.pages, defaultValue_one: "{{count}} page", defaultValue_other: "{{count}} pages" })}</span>}
                  </span>
                )}
              </div>
            )}
            {(draft.cover || coverBusy) && (
              <div className="flex w-full items-center gap-3 text-[var(--foreground)]">
                {draft.cover ? <img data-testid="book-composer-cover-preview" src={draft.cover.url} alt="" className="h-14 w-10 shrink-0 rounded object-cover" /> : <span className="h-14 w-10 shrink-0" />}
                <span className="min-w-0 flex-1 text-sm text-[var(--foreground-muted)]">{coverBusy ? t("book_composer_cover_uploading", "Uploading…") : t("book_composer_cover_set", "Cover set")}</span>
                {draft.cover && !coverBusy && (
                  <button type="button" data-testid="book-composer-cover-remove" className="text-sm font-bold text-[var(--primary)] dark:text-[var(--primary-on-surface)]" onClick={() => patch({ cover: null })}>{t("book_composer_remove", "Remove")}</button>
                )}
              </div>
            )}
            {(draft.photos.length > 0 || photosBusy) && (
              <div data-testid="book-composer-photos" className="flex w-full flex-wrap items-center gap-2">
                {draft.photos.map((ph, i) => (
                  <div key={ph.fileId} className="relative">
                    <img data-testid="book-composer-photo-preview" src={ph.url} alt="" className="h-14 w-14 rounded object-cover" />
                    <button type="button" data-testid={`book-composer-photo-remove-${i}`} aria-label={t("book_composer_remove", "Remove")}
                      className="absolute -right-1 -top-1 flex size-5 items-center justify-center rounded-full bg-[var(--bg-surface)] text-[var(--foreground)] shadow"
                      onClick={() => patch({ photos: draft.photos.filter((_, j) => j !== i) })}><X size={12} aria-hidden /></button>
                  </div>
                ))}
                {photosBusy && <span className="text-sm text-[var(--foreground-muted)]">{t("book_composer_cover_uploading", "Uploading…")}</span>}
              </div>
            )}
            <div className="flex flex-wrap justify-center gap-2">
              <button type="button" data-testid="book-composer-photos-add" disabled={photosBusy} className={PILL} onClick={() => photoInput.current?.click()}>
                {t("book_composer_add_photos", "Add photos")}
              </button>
              <button type="button" data-testid="book-composer-pdf" disabled={uploadingPdf} className={PILL} onClick={() => pdfInput.current?.click()}>
                {draft.pdf ? t("book_composer_replace_pdf", "Replace PDF") : t("book_composer_add_pdf", "Add PDF")}
              </button>
              <button type="button" data-testid="book-composer-cover" disabled={coverBusy} className={PILL} onClick={() => coverInput.current?.click()}>
                {draft.cover ? t("book_composer_replace_cover", "Replace cover") : t("book_composer_add_cover", "Add cover photo")}
              </button>
            </div>
            <input data-testid="book-composer-pdf-input" ref={pdfInput} type="file" accept=".pdf,application/pdf" hidden
              onChange={(e) => { void pickPdf(e.target.files?.[0]); e.target.value = ""; }} />
            <input data-testid="book-composer-photos-input" ref={photoInput} type="file" accept={IMAGE_ACCEPT} multiple hidden
              onChange={(e) => { void pickPhotos(e.target.files); e.target.value = ""; }} />
            {photosFailed && <Err id="photos">{t("book_composer_photos_failed", "A photo didn't upload — try again")}</Err>}
            <input data-testid="book-composer-cover-input" ref={coverInput} type="file" accept={IMAGE_ACCEPT} hidden
              onChange={(e) => { void pickCover(e.target.files?.[0]); e.target.value = ""; }} />
            {!draft.cover && !coverBusy && <p className="m-0 text-xs text-[var(--foreground-subtle)]">{t("book_composer_cover_hint", "Without a cover, the PDF's first page is used")}</p>}
            {(shown.pdf || pdfProblem) && <Err id="pdf">{pdfErrorText}</Err>}
            {coverFailed && <Err id="cover">{coverFailed === "svg" ? t("image_svg_too_large", "This SVG is over 1 MB") : t("book_composer_cover_failed", "The cover didn't upload — try again")}</Err>}
          </div>

          <p className={`m-0 ${SECTION}`}>{t("book_composer_original", "Original")}</p>
          {row("originalAuthor", User, t("book_composer_original_author", "Original author"), { err: "originalAuthor", errText: t("book_composer_original_author_required", "Add the original author"), max: 120, placeholder: `${t("book_composer_original_author", "Original author")} *` })}
          {row("originalYear", Calendar, t("book_composer_year", "Year"), { err: "originalYear", errText: t("book_composer_year_invalid", "Enter a year, e.g. 1999"), inputMode: "numeric", max: 4 })}
          {row("originalLanguage", Languages, t("book_composer_original_language", "Original language"), { err: "originalLanguage", errText: t("book_composer_original_language_required", "Add the original language"), max: 64, placeholder: `${t("book_composer_original_language", "Original language")} *` })}

          <p className={`m-0 ${SECTION}`}>{t("book_composer_translation", "Translation")}</p>
          {row("translator", User, t("book_composer_translator", "Translator"), { max: 120 })}
          {row("translationYear", Calendar, t("book_composer_year", "Year"), { err: "translationYear", errText: t("book_composer_year_invalid", "Enter a year, e.g. 1999"), inputMode: "numeric", max: 4 })}
          {row("translationLanguage", Languages, t("book_composer_translation_language", "Translation language"), { max: 120 })}

          {row("pages", BookOpen, t("book_composer_pages", "Pages"), { err: "pages", errText: t("book_composer_pages_invalid", "Enter a whole number of pages"), inputMode: "numeric", max: 6 })}
          {row("recommendedAge", Baby, t("book_composer_age", "Recommended age"), { max: 16, placeholder: t("book_composer_age_ph", "Recommended age: 12+") })}
          {row("price", Euro, t("book_composer_price", "Price (€)"), { err: "price", errText: t("book_composer_price_invalid", "Enter a price like 12,50 — or leave empty for free"), inputMode: "decimal", max: 14, placeholder: t("book_composer_price_ph", "12,00 · leave empty for free") })}
          {row("shopUrl", ShoppingBag, t("book_composer_shop", "Shop link"), { err: "shopUrl", errText: t("book_composer_shop_invalid", "Enter a web link starting with https://"), inputMode: "url", max: 2048, placeholder: t("book_composer_shop_ph", "Shop link (where to buy it)") })}
          {row("supportUrl", HandHeart, t("book_composer_support", "Support link"), { err: "supportUrl", errText: t("book_composer_support_invalid", "Enter a web link starting with https://"), inputMode: "url", max: 2048, placeholder: t("book_composer_support_ph", "Support link (other method)") })}

          {submitText && <p data-testid="book-composer-submit-error" role="alert" className="m-0 text-sm text-[var(--destructive-text)]">{submitText}</p>}
        </div>
      </div>
      {discardDialog}
    </div>
  );
}
